import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { TextField, Select, MenuItem } from "@material-ui/core";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../UserDashboard/dashboardConfig";

// Tabela listy kontrolnej (Eksport / Import / Tranzyt). Komponent jest czysto prezentacyjny:
// cała logika (uprawnienia, zapis, blokady pól) zostaje w liście i jest przekazywana propsami.
// Kontener tabeli trafia do eksportu PDF (html2canvas), dlatego używamy zwykłych stylów
// bez efektów zależnych od hovera i bez pozycjonowania lepkiego.

const GOOD = COLORS.success;
const BAD = COLORS.danger;

const useStyles = makeStyles((theme) => ({
  wrap: {
    marginTop: theme.spacing(2),
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    background: "#fff",
  },
  table: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
  th: {
    padding: theme.spacing(1.25, 1.5),
    background: "#f5f8fc",
    borderBottom: `2px solid ${COLORS.primary}`,
    fontSize: "0.85em",
    fontWeight: 800,
    color: "#37474f",
    textAlign: "center",
    letterSpacing: 0.3,
  },
  thLeft: { textAlign: "left" },
  groupRow: {
    padding: theme.spacing(1, 1.5),
    background: COLORS.primarySoft,
    borderTop: `1px solid #bbdefb`,
    borderBottom: `1px solid #bbdefb`,
    borderLeft: `5px solid ${COLORS.primary}`,
    color: COLORS.primaryDark,
    fontWeight: 800,
    fontSize: "0.95em",
  },
  groupCount: {
    marginLeft: theme.spacing(1.5),
    padding: "1px 10px",
    borderRadius: 12,
    background: "#fff",
    color: COLORS.primaryDark,
    fontSize: "0.8em",
    fontWeight: 700,
  },
  groupCountDone: { background: GOOD, color: "#fff" },
  td: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${COLORS.borderSoft}`,
    verticalAlign: "middle",
  },
  tdCenter: { textAlign: "center" },
  row: { background: "#fff" },
  rowDone: { background: "#f6fbf6" },
  rowBad: { background: "#fff5f5" },
  rowAccent: { borderLeft: "5px solid transparent" },
  accentDone: { borderLeftColor: "#a5d6a7" },
  accentGood: { borderLeftColor: GOOD },
  accentBad: { borderLeftColor: BAD },
  check: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  number: {
    flexShrink: 0,
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eceff1",
    color: "#546e7a",
    fontSize: "0.8em",
    fontWeight: 800,
  },
  numberDone: { background: GOOD, color: "#fff" },
  numberBad: { background: BAD, color: "#fff" },
  checkText: {
    fontWeight: 600,
    color: "#212121",
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  prep: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  who: { fontSize: "0.72em", color: "#757575", minHeight: "1.2em" },
  toggle: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: "2px solid #b0bec5",
    background: "#fff",
    color: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    transition: "background-color 0.15s, border-color 0.15s",
    "&:hover:not(:disabled)": { borderColor: COLORS.primary },
    "&:disabled": { cursor: "not-allowed", opacity: 0.55 },
  },
  toggleOn: { background: GOOD, borderColor: GOOD, color: "#fff" },
  field: { width: "100%" },
  segment: {
    display: "inline-flex",
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #b0bec5",
  },
  segBtn: {
    width: 40,
    height: 34,
    border: "none",
    background: "#fff",
    color: "#90a4ae",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    transition: "background-color 0.15s, color 0.15s",
    "&:first-child": { borderRight: "1px solid #b0bec5" },
    "&:hover:not(:disabled)": { background: "#f5f5f5" },
    "&:disabled": { cursor: "not-allowed", opacity: 0.55 },
  },
  segGood: {
    background: GOOD,
    color: "#fff",
    "&:hover:not(:disabled)": { background: GOOD },
  },
  segBad: {
    background: BAD,
    color: "#fff",
    "&:hover:not(:disabled)": { background: BAD },
  },
}));

const stripNumber = (text) => String(text || "").replace(/^\d+\.\s*/, "");

const Segment = ({
  classes,
  value,
  onGood,
  onBad,
  disabledGood,
  disabledBad,
}) => (
  <div className={classes.segment}>
    <button
      type="button"
      className={`${classes.segBtn} ${value?.good ? classes.segGood : ""}`}
      onClick={onGood}
      disabled={disabledGood}
      aria-label="Poprawnie"
      aria-pressed={Boolean(value?.good)}
    >
      <CheckIcon fontSize="small" />
    </button>
    <button
      type="button"
      className={`${classes.segBtn} ${value?.bad ? classes.segBad : ""}`}
      onClick={onBad}
      disabled={disabledBad}
      aria-label="Niepoprawnie"
      aria-pressed={Boolean(value?.bad)}
    >
      <CloseIcon fontSize="small" />
    </button>
  </div>
);

/**
 * Props:
 *  rows, groups [{ title, from, to }], data, textIds, selectIds, selectOptions,
 *  isFieldDisabled(rowId), onTextChange(rowId, value), onSelectChange(rowId, value),
 *  onTogglePrepared(rowId, checked), onVerify(rowId, "good"|"bad"),
 *  isVerifyDisabled(type), onInternalControl(rowId, "good"|"bad"), isInternalDisabled
 */
const ChecklistTable = ({
  rows,
  groups,
  data,
  textIds,
  selectIds,
  selectOptions,
  isFieldDisabled,
  onTextChange,
  onSelectChange,
  onTogglePrepared,
  onVerify,
  isVerifyDisabled,
  onInternalControl,
  isInternalDisabled,
}) => {
  const classes = useStyles();

  const preparedWho = (id) =>
    data.textFields[id]?.verifyingPerson ||
    data.selectFields[id]?.verifyingPerson ||
    data.checkboxesPreparation[id]?.verifyingPerson ||
    "";

  const isPrepared = (id) => {
    const text = data.textFields[id]?.value;
    const select = data.selectFields[id]?.value;
    return Boolean(
      (text && text !== "0") ||
      (select && select !== "0") ||
      data.checkboxesPreparation[id]?.verifying,
    );
  };

  const renderRow = (row) => {
    const prepared = isPrepared(row.id);
    const verification = data.checkboxesVerification[row.id];
    const internal = data.checkboxesInternalControl[row.id];
    const bad = Boolean(verification?.bad || internal?.bad);
    const good = Boolean(verification?.good);
    const rowClass = bad
      ? classes.rowBad
      : prepared
        ? classes.rowDone
        : classes.row;
    const accent = bad
      ? classes.accentBad
      : good
        ? classes.accentGood
        : prepared
          ? classes.accentDone
          : "";

    return (
      <tr key={row.id} className={rowClass}>
        <td className={`${classes.td} ${classes.rowAccent} ${accent}`}>
          <div className={classes.check}>
            <span
              className={`${classes.number} ${bad ? classes.numberBad : prepared ? classes.numberDone : ""}`}
            >
              {row.id}
            </span>
            <span className={classes.checkText}>{stripNumber(row.check)}</span>
          </div>
        </td>
        <td className={`${classes.td} ${classes.tdCenter}`}>
          <div className={classes.prep}>
            {textIds.includes(row.id) ? (
              <TextField
                className={classes.field}
                value={data.textFields[row.id]?.value || ""}
                onChange={(e) => onTextChange(row.id, e.target.value)}
                variant="outlined"
                size="small"
                disabled={isFieldDisabled(row.id)}
              />
            ) : selectIds.includes(row.id) ? (
              <Select
                className={classes.field}
                value={data.selectFields[row.id]?.value || ""}
                onChange={(e) => onSelectChange(row.id, e.target.value)}
                variant="outlined"
                displayEmpty
                disabled={isFieldDisabled(row.id)}
              >
                {selectOptions[row.id].map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <button
                type="button"
                className={`${classes.toggle} ${
                  data.checkboxesPreparation[row.id]?.verifying
                    ? classes.toggleOn
                    : ""
                }`}
                onClick={() =>
                  onTogglePrepared(
                    row.id,
                    !data.checkboxesPreparation[row.id]?.verifying,
                  )
                }
                disabled={isFieldDisabled(row.id)}
                aria-label={`Sprawdzono: ${stripNumber(row.check)}`}
                aria-pressed={Boolean(
                  data.checkboxesPreparation[row.id]?.verifying,
                )}
              >
                <CheckIcon fontSize="small" />
              </button>
            )}
            <span className={classes.who}>{preparedWho(row.id)}</span>
          </div>
        </td>
        <td className={`${classes.td} ${classes.tdCenter}`}>
          <Segment
            classes={classes}
            value={verification}
            onGood={() => onVerify(row.id, "good")}
            onBad={() => onVerify(row.id, "bad")}
            disabledGood={isVerifyDisabled("good")}
            disabledBad={isVerifyDisabled("bad")}
          />
        </td>
        <td className={`${classes.td} ${classes.tdCenter}`}>
          <Segment
            classes={classes}
            value={internal}
            onGood={() => onInternalControl(row.id, "good")}
            onBad={() => onInternalControl(row.id, "bad")}
            disabledGood={isInternalDisabled}
            disabledBad={isInternalDisabled}
          />
        </td>
      </tr>
    );
  };

  return (
    <div className={classes.wrap}>
      <table className={classes.table}>
        <colgroup>
          <col />
          <col style={{ width: "26%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "14%" }} />
        </colgroup>
        <thead>
          <tr>
            <th className={`${classes.th} ${classes.thLeft}`}>Sprawdź</th>
            <th className={classes.th}>Przygotowanie</th>
            <th className={classes.th}>Weryfikacja</th>
            <th className={classes.th}>Kontrola wewnętrzna</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const groupRows = rows.filter(
              (r) => r.id >= group.from && r.id <= group.to,
            );
            const done = groupRows.filter((r) => isPrepared(r.id)).length;
            return (
              <React.Fragment key={group.title}>
                <tr>
                  <td colSpan={4} className={classes.groupRow}>
                    {group.title}
                    <span
                      className={`${classes.groupCount} ${
                        done === groupRows.length ? classes.groupCountDone : ""
                      }`}
                    >
                      {done} / {groupRows.length}
                    </span>
                  </td>
                </tr>
                {groupRows.map(renderRow)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ChecklistTable;
